import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes, type Theme } from '@mui/material/styles';
import { type ReactNode } from 'react';

import AuroraBackdrop from '@/components/AuroraBackdrop';
import BrandLockup from '@/components/BrandLockup';
import AuthPartnerHighlights from '@/domain/auth/components/AuthPartnerHighlights';
import { type AuthPageVariant } from '@/domain/auth/types';
import { useTranslate } from '@/state/i18n';
import { controlTokens, partnerTokens } from '@/theme/themePrimitives';

interface Props {
  children: ReactNode;
  variant: AuthPageVariant;
}

export default function AuthPageLayout({ children, variant }: Props) {
  const t = useTranslate('domain.auth.components.AuthPageLayout');
  const isSignup = variant === 'signup';

  return (
    <Box sx={sx.page}>
      <AuroraBackdrop />
      <Box sx={sx.grid}>
        <Box component="section" sx={sx.stage}>
          <BrandLockup descriptor={t('brandTag')} size="stage" />

          <Box sx={sx.copy}>
            <Typography component="p" variant="overline" sx={sx.eyebrow}>
              {t('eyebrow')}
            </Typography>
            <Typography key={variant} component="h1" variant="h1" sx={sx.title}>
              {t(isSignup ? 'signupTitle' : 'signinTitle')}
              <Box component="span" sx={sx.highlight}>
                {t(isSignup ? 'signupHighlight' : 'signinHighlight')}
              </Box>
            </Typography>
          </Box>

          <Box sx={sx.highlightsArea}>
            <AuthPartnerHighlights />
          </Box>
        </Box>

        <Box component="main" sx={sx.formColumn}>
          <Box sx={sx.formSlot}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}

const rise = keyframes`
  from { transform: translateY(14px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const sx = {
  page: (theme: Theme) => ({
    minHeight: '100dvh',
    position: 'relative' as const,
    isolation: 'isolate' as const,
    overflowX: 'clip' as const,
    // Partners Soft Blue floor (Signal PD2); the aurora on top is AuroraBackdrop.
    bgcolor: partnerTokens.bar,
    WebkitFontSmoothing: 'antialiased',
    textRendering: 'optimizeLegibility' as const,
    '@media (prefers-reduced-motion: reduce)': {
      '& *, & *::before, & *::after': {
        animationDuration: '0.01ms !important',
        animationIterationCount: '1 !important',
      },
    },
    ...theme.applyStyles('dark', { bgcolor: 'background.default' }),
  }),
  grid: {
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto' },
    maxWidth: { md: 1440 },
    mx: 'auto',
  },
  // On desktop the stage stays put while a long form scrolls beside it.
  stage: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: { xs: 560, md: 'none' },
    mx: { xs: 'auto', md: 0 },
    px: { xs: '20px', sm: 4, lg: '72px' },
    pt: { xs: '14px', sm: 3, lg: '34px' },
    pb: { md: 4 },
    position: { md: 'sticky' },
    top: { md: 0 },
    alignSelf: { md: 'start' },
    height: { md: '100dvh' },
  },
  copy: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: { xs: '12px', lg: '18px' },
    mt: { xs: 2, md: 5, lg: '54px' },
    maxWidth: 780,
  },
  eyebrow: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    m: 0,
    fontSize: { xs: 11, lg: 12 },
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: '0.14em',
    color: controlTokens.primary,
    ...theme.applyStyles('dark', { color: 'text.primary' }),
  }),
  title: (theme: Theme) => ({
    m: 0,
    fontSize: { xs: '1.5rem', sm: '2rem', md: 'clamp(1.75rem, 3.1vw, 2.75rem)' },
    lineHeight: { xs: 1.12, md: 1.06 },
    letterSpacing: { xs: '-0.035em', md: '-0.04em' },
    color: controlTokens.primary,
    animation: `${rise} 600ms cubic-bezier(0.2, 0.7, 0.2, 1) both`,
    ...theme.applyStyles('dark', { color: 'text.primary' }),
  }),
  highlight: (theme: Theme) => ({
    display: 'block',
    color: partnerTokens.ink,
    ...theme.applyStyles('dark', { color: partnerTokens.softBlue }),
  }),
  highlightsArea: {
    width: '100%',
    maxWidth: { xs: 560, md: 520 },
    mt: { xs: 3, md: 6 },
    mb: { xs: 2, md: 0 },
  },
  formColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: { md: 'center' },
    minHeight: { md: '100dvh' },
    px: { xs: '20px', sm: 4, md: 0 },
    pl: { md: 1, lg: 3 },
    pr: { md: 4, lg: '72px' },
    pt: { xs: 1, md: '28px' },
    pb: { xs: 4, md: '28px' },
  },
  formSlot: {
    width: '100%',
    maxWidth: { xs: 560, md: 'none' },
    display: 'flex',
    flexDirection: 'column',
    animation: `${rise} 800ms cubic-bezier(0.22, 1, 0.36, 1) 0.15s both`,
  },
};
